import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CollectionSidebarPrice } from './CollectionSidebarPrice';
import { useSearch } from '@src/contexts/SearchProvider';
import { useCollection } from '@src/contexts/CollectionContext';
import { FilterSkeleton } from './CollectionSkeletons';
import {
  extractReadableValue,
  getDisplayLabel,
  getReadableFilterValue,
  createFilterValueMapping,
  getDisplayValueForActiveFilter,
  getFilterValueForStorage,
  getDisplayLabelForStoredValue
} from '@src/utils/filterUtils';
import { useSearchContext } from '@src/contexts/SearchContext';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

export const CollectionSidebar = ({
  filters,
  metaobject_filters,
  toggleSidebar,
  setSidebarToggle,
  sidebarPosition = 'left',
  show_filter_dividers = true,
  filter_divider_style = 'border-bottom: 1px solid #e5e7eb;'
}) => {
  const search = useSearch();
  let isSearchSidebar = false;
  let context;
  try {
    context = useSearchContext();
    isSearchSidebar = true; // If we successfully get context, it's a search sidebar
  } catch (e) {
    context = useCollection();
    isSearchSidebar = false; // Otherwise, it's a collection sidebar
  }
  const { activeFilters, applyFilters, loading, totalResults } = context;
  const { isMobile } = useResponsiveBreakpoints();

  // Manage sidebar visibility internally
  const [isVisible, setIsVisible] = useState(false);

  const [animationClass, setAnimationClass] = useState(
    sidebarPosition === 'left' ? '-translate-x-full' : 'translate-x-full'
  );
  const [shouldRender, setShouldRender] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Internal toggle function
  const internalToggleSidebar = useCallback(() => {
    setIsVisible((prev) => !prev);
  }, []);

  // Register the toggle function with the parent component
  useEffect(() => {
    if (setSidebarToggle) {
      setSidebarToggle(internalToggleSidebar);
    }
  }, [setSidebarToggle, internalToggleSidebar]);

  // New: Toggle for filter application mode
  const [applyInstantly, setApplyInstantly] = useState(false);
  // New: Local state for pending filters when not applying instantly
  const [pendingFilters, setPendingFilters] = useState({});
  // Price range state for CollectionSidebarPrice component
  const [priceRanges, setPriceRanges] = useState({});
  // Store original price bounds for each filter to preserve full range
  const [originalPriceBounds, setOriginalPriceBounds] = useState({});

  // Memoized price range setter to prevent infinite re-renders
  const handleSetPriceRange = useCallback((filterKey, range) => {
    setPriceRanges((prev) => ({ ...prev, [filterKey]: range }));
  }, []);

  // Store original price bounds when filters are first loaded
  useEffect(() => {
    if (filters && filters.length > 0) {
      const newOriginalBounds = {};

      filters.forEach((filter) => {
        // Check if this is a price filter
        if (
          filter.key === 'Price' ||
          filter.key === 'price' ||
          filter.type === 'PRICE' ||
          filter.type === 'PRICE_RANGE' ||
          filter.key === 'filter.v.price' ||
          filter.key?.includes('price') ||
          filter.label?.toLowerCase().includes('price')
        ) {
          // Extract price values to determine original bounds
          const priceValues = filter.values
            .map((value) => {
              if (value.input) {
                // Handle string input that needs parsing
                if (typeof value.input === 'string') {
                  try {
                    const parsedInput = JSON.parse(value.input);
                    if (parsedInput.price) {
                      return { min: parsedInput.price.min, max: parsedInput.price.max };
                    }
                  } catch (e) {
                    // Silent fallback
                  }
                }
                // Handle already parsed object
                else if (typeof value.input === 'object' && value.input.price) {
                  return { min: value.input.price.min, max: value.input.price.max };
                }
              }

              // Fallback to parsing the key field
              try {
                const parsed = JSON.parse(value.input);
                if (parsed.price) {
                  return { min: parsed.price.min, max: parsed.price.max };
                }
              } catch (e) {
                // Handle non-JSON price values
                const parts = value.input.split('-');
                if (parts.length === 2) {
                  const minPrice = parseFloat(parts[0]);
                  const maxPrice = parseFloat(parts[1]);
                  if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                    return { min: minPrice, max: maxPrice };
                  }
                }

                // Try to extract from label or other formats
                if (value.label && value.label !== 'Price') {
                  const labelParts = value.label.split('-');
                  if (labelParts.length === 2) {
                    const minPrice = parseFloat(labelParts[0].replace(/[^0-9.]/g, ''));
                    const maxPrice = parseFloat(labelParts[1].replace(/[^0-9.]/g, ''));
                    if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                      return { min: minPrice, max: maxPrice };
                    }
                  }
                }
              }

              return null;
            })
            .filter(Boolean);

          if (priceValues.length > 0) {
            // Only set original bounds if not already set (preserve the first unfiltered data)
            if (!originalPriceBounds[filter.key]) {
              newOriginalBounds[filter.key] = {
                min: Math.min(...priceValues.map((p) => p.min)),
                max: Math.max(...priceValues.map((p) => p.max))
              };
            }
          }
        }
      });

      // Only update if we have new bounds to set
      if (Object.keys(newOriginalBounds).length > 0) {
        setOriginalPriceBounds((prev) => ({ ...prev, ...newOriginalBounds }));
      }
    }
  }, [filters]); // Only depend on filters, not activeFilters

  // Add styles for price slider
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
            input[type="range"] {
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
                background: transparent;
                cursor: pointer;
                height: 10px;
                outline: none;
                border: none;
                padding: 0;
                margin: 0;
                width: 100%;
            }
            input[type="range"]::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background: white;
                cursor: pointer;
                border: 2px solid black;
                box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                position: relative;
                margin-top: -7px;
                margin-left: 0;
                margin-right: 0;
            }
            input[type="range"]::-moz-range-thumb {
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background: white;
                cursor: pointer;
                border: 2px solid black;
                box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                position: relative;
                -moz-appearance: none;
                margin-top: -7px;
                margin-left: 0;
                margin-right: 0;
                border-width: 2px;
            }
            input[type="range"]::-webkit-slider-track {
                background: transparent;
                width: 100%;
                height: 2px;
                cursor: pointer;
                border-radius: 1px;
                border: none;
                margin: 0;
                padding: 0;
            }
            input[type="range"]::-moz-range-track {
                background: transparent;
                width: 100%;
                height: 2px;
                cursor: pointer;
                border-radius: 1px;
                border: none;
                margin: 0;
                padding: 0;
            }
            input[type="range"]:focus {
                outline: none;
            }
            input[type="range"]:focus::-webkit-slider-thumb {
                box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
            }
            input[type="range"]:focus::-moz-range-thumb {
                box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
            }
        `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Handle sidebar animation with proper mount/unmount timing
  useEffect(() => {
    if (isVisible && !isClosing) {
      setShouldRender(true);
      // Small delay to trigger animation
      setTimeout(() => setAnimationClass('translate-x-0'), 10);
    } else if (!isVisible && !shouldRender) {
      // This handles external closure (like from parent)
      handleInternalClose();
    }
  }, [isVisible]);

  // Initialize filters from URL on component mount
  useEffect(() => {
    const initializeFiltersFromURL = () => {
      const params = new URLSearchParams(window.location.search);
      const urlFilters = {};

      // Parse filter parameters from URL
      for (const [key, value] of params) {
        if (key.startsWith('filter.')) {
          const filterKey = key.substring(7); // Remove 'filter.' prefix
          if (!urlFilters[filterKey]) {
            urlFilters[filterKey] = [];
          }
          urlFilters[filterKey].push(value);
        }
      }

      // Apply URL filters if any exist
      if (Object.keys(urlFilters).length > 0) {
        console.log('CollectionSidebar: Initializing filters from URL:', urlFilters);
        applyFilters(urlFilters);

        // Dispatch search request event to trigger initial filtering
        window.dispatchEvent(
          new CustomEvent('searchrequest-filter-applied', {
            detail: {
              filters_object: urlFilters,
              type: isSearchSidebar ? 'search' : 'plp'
            }
          })
        );
      }
    };

    // Only initialize once when component mounts
    initializeFiltersFromURL();
  }, []); // Empty dependency array means this runs once on mount

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      console.log('CollectionSidebar: Browser back/forward detected');

      // Parse filters from the current URL
      const params = new URLSearchParams(window.location.search);
      const urlFilters = {};

      for (const [key, value] of params) {
        if (key.startsWith('filter.')) {
          const filterKey = key.substring(7); // Remove 'filter.' prefix
          if (!urlFilters[filterKey]) {
            urlFilters[filterKey] = [];
          }
          urlFilters[filterKey].push(value);
        }
      }

      // Apply the filters from URL
      console.log('CollectionSidebar: Applying filters from URL navigation:', urlFilters);
      applyFilters(urlFilters);

      // Dispatch search request event
      window.dispatchEvent(
        new CustomEvent('searchrequest-filter-applied', {
          detail: {
            filters_object: urlFilters,
            type: isSearchSidebar ? 'search' : 'plp'
          }
        })
      );
    };

    // Add event listener for browser navigation
    window.addEventListener('popstate', handlePopState);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [applyFilters]); // Include applyFilters in dependencies

  // Handle internal close with animation
  const handleInternalClose = () => {
    if (isClosing) return; // Prevent multiple close calls

    setIsClosing(true);
    setAnimationClass(sidebarPosition === 'left' ? '-translate-x-full' : 'translate-x-full');

    // Wait for animation to complete before unmounting
    setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      setIsVisible(false); // Set internal visibility to false
    }, 300);
  };

  let activeFilterCount = 0;
  if (activeFilters) {
    // Count active filters
    activeFilterCount = Object?.keys(activeFilters).reduce((count, key) => {
      return count + (activeFilters[key]?.length || 0);
    }, 0);
  }

  // Check if a filter value is currently selected
  const isFilterSelected = (filterKey, valueKey) => {
    const filterState = applyInstantly ? activeFilters : pendingFilters;
    const filterValues = filterState[filterKey] || [];
    const storageValue = getFilterValueForStorage(filterKey, valueKey, filters);

    return filterValues.includes(storageValue);
  };

  // Handle filter selection
  const handleFilterChange = (filterKey, valueKey) => {
    // Always update pendingFilters for UI
    const baseFilters = { ...(applyInstantly ? activeFilters : pendingFilters) };
    if (!baseFilters[filterKey]) {
      baseFilters[filterKey] = [];
    }
    const valueForStorage = getFilterValueForStorage(filterKey, valueKey, filters);
    if (baseFilters[filterKey].includes(valueForStorage)) {
      baseFilters[filterKey] = baseFilters[filterKey].filter((v) => v !== valueForStorage);
      if (baseFilters[filterKey].length === 0) {
        delete baseFilters[filterKey];
      }
    } else {
      baseFilters[filterKey].push(valueForStorage);
    }
    setPendingFilters(baseFilters);
    if (applyInstantly) {
      applyFilters(baseFilters);
      window.dispatchEvent(
        new CustomEvent('searchrequest-filter-applied', {
          detail: {
            filters_object: baseFilters,
            type: isSearchSidebar ? 'search' : 'plp'
          }
        })
      );
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    if (applyInstantly) {
      applyFilters({});
      const params = new URLSearchParams(window.location.search);
      const keysToRemove: string[] = [];
      for (const [key] of params) {
        if (key.startsWith('filter.')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => params.delete(key));
      const newURL = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({ filters: {} }, '', newURL);
      window.dispatchEvent(
        new CustomEvent('searchrequest-filter-applied', {
          detail: {
            filters_object: {},
            type: isSearchSidebar ? 'search' : 'plp'
          }
        })
      );
    } else {
      setPendingFilters({});
    }
  };

  // Memoization cache for parsed filter values to prevent infinite loops
  const filterValueCache = useRef(new Map());

  // Helper function to parse filter values and get readable labels
  const getReadableFilterValueLocal = useCallback(
    (filterKey, value) => {
      // Get display label using the shared utility function
      let result = getReadableFilterValue(filterKey, value, filters);

      // Cache the result to prevent re-computation
      const cacheKey = `${filterKey}:${value}`;
      filterValueCache.current.set(cacheKey, result);
      return result;
    },
    [filters]
  );

  // (removed duplicate declaration)

  const FilterList = ({ filter, metaobjectFilter }) => (
    <div
      className='py-[24px] last:border-b-0'
      style={{ order: metaobjectFilter.sort_order }}
    >
      {/* <div className='b7--medium mb-4'>{metaobjectFilter.display_name}</div> */}
      <div className='filters__list b7--medium flex flex-col space-y-3'>
        {filter.values.map((value, index) => {
          const selected = isFilterSelected(filter.key, value.key);
          return (
            <div
              key={value.key}
              className='flex items-center gap-2'
            >
              <input
                type='checkbox'
                id={value.key}
                onChange={(e) => {
                  e.stopPropagation();
                  handleFilterChangeWithFeedback(filter.key, value.key);
                }}
                className='appearance-none w-4 h-4 border border-black rounded checked:bg-checkbox-checked bg-cover'
                checked={selected}
              />

              <label
                className='b8--medium'
                title={value.label}
                htmlFor={value.key}
              >
                {value.label}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );

  const FilterButton = ({ filter, metaobjectFilter }) => {
    const [visibleCount, setVisibleCount] = useState(metaobjectFilter.display_limit);
    const [showMore, setShowMore] = useState(true);
    const handleShowMore = () => {
      setVisibleCount((prevCount) => prevCount + metaobjectFilter.display_limit);
      setShowMore(false);
    };
    const handleShowLess = () => {
      setVisibleCount(metaobjectFilter.display_limit);
      setShowMore(true);
    };
    const visibleButtons = metaobjectFilter.filter_buttons.slice(0, visibleCount);
    const hasMore = visibleCount < metaobjectFilter.filter_buttons.length;
    return (
      <div
        className='flex flex-col pb-6 mb-6 last:border-b-0'
        style={{ order: metaobjectFilter.sort_order }}
      >
        {/* <div className='b7--medium mb-4'>{metaobjectFilter.display_name}</div> */}
        <div className='grid grid-cols-2 gap-[16px]'>
          {visibleButtons.map((value) => {
            const selected = isFilterSelected(filter.key, value.key);
            return (
              <button
                key={value.key}
                className={`filters__button px-3 py-2 rounded border ${
                  selected ? 'bg-primary text-white border-primary' : 'bg-white text-black border-gray-300'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleFilterChangeWithFeedback(filter.key, value.key);
                }}
                aria-pressed={selected}
              >
                {value.label}
              </button>
            );
          })}
        </div>
        {hasMore ? (
          <button
            className='filters__show-hide mt-4 font-medium'
            onClick={handleShowMore}
          >
            {`Show All ${metaobjectFilter.display_name}s (${filter.values.length})`}
          </button>
        ) : !showMore ? (
          <button
            className='filters__show-hide mt-4 text-primary hover:text-primary-dark transition-colors duration-200 font-medium'
            onClick={handleShowLess}
          >
            Show Less
          </button>
        ) : null}
      </div>
    );
  };

  const FilterColour = ({ filter }) => {
    // Function to extract swatch data from filter value
    const getSwatchData = (value) => {
      // First priority: Use swatch.color from API if available
      if (value.swatch && value.swatch.color) {
        return {
          type: 'color',
          value: value.swatch.color,
          image: value.swatch?.image?.previewImage?.url
        };
      }

      let colorValue = null;

      // Try to parse JSON input second
      if (value.input && typeof value.input === 'object') {
        const input = value.input;

        // Check for product metafield (color_name, colour_name, etc.)
        if (input.productMetafield) {
          colorValue = input.productMetafield.value;
        } else if (input.variantOption) {
          colorValue = input.variantOption.value;
        } else if (input.tag) {
          // Handle tag-based colors
          colorValue = input.tag.replace(/color[:-]?/i, '').replace(/colour[:-]?/i, '');
        }
      }

      // Fallback to label
      if (!colorValue) {
        colorValue = value.label;
      }

      // Check if we have a valid color value for parsing
      if (colorValue && isColorValue(colorValue)) {
        return {
          type: 'color',
          value: getColorCSSValue(colorValue)
        };
      }

      // Fallback to CSS class based swatch
      return {
        type: 'css',
        value: colorValue ? String(colorValue).toLowerCase().replace(/\s+/g, '-') : 'default'
      };
    };

    // Helper function to detect if a value is a color
    const isColorValue = (value) => {
      if (!value) return false;
      const str = value.toString().toLowerCase();

      // Check for hex colors
      if (/^#[0-9a-f]{3,6}$/i.test(str)) return true;

      // Check for rgb/rgba
      if (/^rgba?\(/i.test(str)) return true;

      // Check for common color names
      const colorNames = [
        'red',
        'blue',
        'green',
        'yellow',
        'purple',
        'pink',
        'orange',
        'brown',
        'black',
        'white',
        'gray',
        'grey',
        'navy',
        'beige',
        'sage',
        'burgundy',
        'lavender',
        'turquoise',
        'coral',
        'mint',
        'cream',
        'ivory',
        'gold',
        'silver',
        'bronze',
        'maroon',
        'olive',
        'lime',
        'aqua',
        'teal',
        'fuchsia',
        'magenta',
        'tan',
        'khaki'
      ];

      return colorNames.some((color) => str.includes(color));
    };

    // Helper function to convert color names to CSS values
    const getColorCSSValue = (colorName) => {
      if (!colorName) return '#cccccc';

      const name = colorName.toLowerCase();

      // Return hex colors as-is
      if (/^#[0-9a-f]{3,6}$/i.test(name)) return name;

      // Return rgb/rgba as-is
      if (/^rgba?\(/i.test(name)) return name;

      // Common color mappings to hex values
      const colorMap = {
        red: '#ff0000',
        blue: '#0000ff',
        green: '#008000',
        yellow: '#ffff00',
        purple: '#800080',
        pink: '#ffc0cb',
        orange: '#ffa500',
        brown: '#a52a2a',
        black: '#000000',
        white: '#ffffff',
        gray: '#808080',
        grey: '#808080',
        navy: '#000080',
        beige: '#f5f5dc',
        sage: '#9caf88',
        burgundy: '#800020',
        lavender: '#e6e6fa',
        turquoise: '#40e0d0',
        coral: '#ff7f50',
        mint: '#98fb98',
        cream: '#fffdd0',
        ivory: '#fffff0',
        gold: '#ffd700',
        silver: '#c0c0c0',
        bronze: '#cd7f32',
        maroon: '#800000',
        olive: '#808000',
        lime: '#00ff00',
        aqua: '#00ffff',
        teal: '#008080',
        fuchsia: '#ff00ff',
        magenta: '#ff00ff',
        tan: '#d2b48c',
        khaki: '#f0e68c'
      };

      // Find matching color
      for (const [key, value] of Object.entries(colorMap)) {
        if (name.includes(key)) {
          return value;
        }
      }

      // Default to a neutral color if no match found
      return '#cccccc';
    };

    const renderSwatchElement = (swatchData, value, selected) => {
      if (swatchData.type === 'color' && swatchData.value) {
        // Render color swatch
        return (
          <>
            {!swatchData.image && (
              <div
                className={`rounded-sm overflow-hidden ${selected ? 'border border-black' : ''} ${
                  swatchData.value.toLowerCase() === '#ffffff' ? 'shadow' : ''
                }`}
              >
                <div
                  className={`filters__colour--item w-4 h-4 `}
                  style={{ backgroundColor: swatchData.value }}
                  title={`Color: ${swatchData.value}`}
                />
              </div>
            )}
            {swatchData.image && (
              <div className={`rounded-sm overflow-hidden ${selected ? 'border border-black' : ''}`}>
                <img
                  src={swatchData.image}
                  className={`filters__colour--item w-4 h-4`}
                  title={`Color: ${swatchData.value}`}
                />
              </div>
            )}
          </>
        );
      } else {
        // Fallback to CSS class based swatch
        return (
          <div
            className={`filters__colour--item filters__colour--${swatchData.value} w-4 h-4`}
            title={`CSS Class: filters__colour--${swatchData.value}`}
          ></div>
        );
      }
    };

    return (
      <div className='py-[24px]'>
        {/* <div className='b7--medium mb-4'>{filter.label}</div> */}
        <div className='grid grid-cols-2 gap-[16px]'>
          {filter.values.map((value) => {
            const selected = isFilterSelected(filter.key, value.key);
            const swatchData = getSwatchData(value);
            return (
              <div
                className={`filters__colour relative cursor-pointer flex flex-row items-center justify-start w-full group ${
                  selected ? 'filters__colour--selected' : ''
                }`}
                key={value.key}
                onClick={(e) => {
                  e.preventDefault();
                  handleFilterChangeWithFeedback(filter.key, value.key);
                }}
              >
                <input
                  type='checkbox'
                  value={value.key}
                  checked={selected}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleFilterChangeWithFeedback(filter.key, value.key);
                  }}
                  className='absolute opacity-0 cursor-pointer'
                />
                {renderSwatchElement(swatchData, value, selected)}
                <span className={`b3 ml-2 text-center b7--medium ${selected ? 'filter-colour--selected' : ''}`}>
                  {value.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const FilterSize = ({ filter, metaobjectFilter }) => {
    const displayLimit = metaobjectFilter.display_limit ? metaobjectFilter.display_limit : filter.values.length;
    const [visibleCount, setVisibleCount] = useState(displayLimit);
    const [showMore, setShowMore] = useState(true);
    const handleShowMore = () => {
      setVisibleCount((prevCount) => prevCount + displayLimit);
      setShowMore(false);
    };
    const handleShowLess = () => {
      setVisibleCount(displayLimit);
      setShowMore(true);
    };
    const visibleFilters = filter.values.slice(0, visibleCount);
    const hasMore = visibleCount < filter.values.length;

    return (
      <div
        className='flex flex-col pb-6 mb-6 last:border-b-0'
        style={{ order: metaobjectFilter.sort_order }}
      >
        {/* <div className='b4--bold text-gray-900 mb-4'>{metaobjectFilter.display_name}</div> */}
        <div className='grid grid-cols-4 gap-2 mt-2'>
          {visibleFilters.map((value) => {
            const selected = isFilterSelected(filter.key, value.key);
            return (
              <div
                className='filters__size relative cursor-pointer h-full w-full group'
                key={value.key}
                onClick={(e) => {
                  e.preventDefault();
                  handleFilterChangeWithFeedback(filter.key, value.key);
                }}
              >
                <input
                  type='checkbox'
                  value={value.key}
                  checked={selected}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleFilterChangeWithFeedback(filter.key, value.key);
                  }}
                  className='absolute opacity-0'
                />
                <button
                  className={`filters__size--button capitalize w-full h-10 flex items-center justify-center text-sm font-medium rounded-md border-2 transition-all duration-300 ${
                    selected ? 'border-primary bg-primary text-white' : ''
                  }`}
                >
                  {value.label}
                </button>
              </div>
            );
          })}
        </div>
        {hasMore ? (
          <button
            className='filters__show-hide mt-4 text-primary hover:text-primary-dark transition-colors duration-200'
            onClick={handleShowMore}
          >
            {`Show All ${metaobjectFilter.display_name}s (${filter.values.length})`}
          </button>
        ) : !showMore ? (
          <button
            className='filters__show-hide mt-4 text-primary hover:text-primary-dark transition-colors duration-200 font-medium'
            onClick={handleShowLess}
          >
            Show Less
          </button>
        ) : null}
      </div>
    );
  };

  const renderFilterValues = (filter, metaobjectFilters) => {
    const metaobjectFilter = metaobjectFilters?.find(
      (value) => value.filter_name?.toLowerCase() === filter.key?.toLowerCase()
    );

    // Handle price filters with slider
    if (
      filter.key === 'Price' ||
      filter.key === 'price' ||
      filter.type === 'PRICE' ||
      filter.type === 'PRICE_RANGE' ||
      filter.key === 'filter.v.price' ||
      filter.key?.includes('price') ||
      filter.label?.toLowerCase().includes('price')
    ) {
      return (
        <CollectionSidebarPrice
          filter={filter}
          activeFilters={activeFilters}
          pendingFilters={pendingFilters}
          setPendingFilters={setPendingFilters}
          applyFilters={applyFilters}
          applyInstantly={applyInstantly}
          isSearchSidebar={isSearchSidebar}
          priceRange={priceRanges[filter.key]}
          setPriceRange={(range) => handleSetPriceRange(filter.key, range)}
          originalMinPrice={originalPriceBounds[filter.key]?.min}
          originalMaxPrice={originalPriceBounds[filter.key]?.max}
        />
      );
    }
    // Handle color filters using the new API structure
    else if (filter.key?.includes('color')) {
      return metaobjectFilter ? (
        <FilterButton
          filter={filter}
          metaobjectFilter={metaobjectFilter}
        />
      ) : (
        <FilterColour filter={filter} />
      );
    }
    // Handle size filters
    else if (filter.type === 'SIZE' || filter.key === 'options.size') {
      return metaobjectFilter ? (
        <FilterSize
          filter={filter}
          metaobjectFilter={metaobjectFilter}
        />
      ) : (
        <FilterSize
          filter={filter}
          metaobjectFilter={{ sort_order: 0, display_name: filter.label }}
        />
      );
    }
    // Handle all other filters with list format
    else {
      return metaobjectFilter ? (
        <FilterList
          filter={filter}
          metaobjectFilter={metaobjectFilter}
        />
      ) : (
        <FilterList
          filter={filter}
          metaobjectFilter={{ sort_order: 0, display_name: filter.label }}
        />
      );
    }
  };

  // Add haptic feedback for mobile devices
  const triggerHapticFeedback = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50); // Light vibration for 50ms
    }
  };

  // Helper function to create a mapping between GID values and their display labels
  const filterValueMapping = useMemo(() => {
    return createFilterValueMapping(filters);
  }, [filters]);

  // Enhanced function to get display value that considers the mapping
  const getDisplayValueForActiveFilterLocal = useCallback(
    (filterKey, value) => {
      return getDisplayValueForActiveFilter(filterKey, value, filterValueMapping, filters);
    },
    [filterValueMapping, filters]
  );

  // Enhanced filter change handler with feedback
  const handleFilterChangeWithFeedback = (filterKey, valueKey) => {
    triggerHapticFeedback();
    handleFilterChange(filterKey, valueKey);
  };
  // Enhanced clear all with feedback
  const clearAllFiltersWithFeedback = () => {
    triggerHapticFeedback();
    clearAllFilters();
  };
  // New: Apply pending filters when Show Products is clicked (if not instant)
  const handleApplyPendingFilters = () => {
    applyFilters(pendingFilters);
    window.dispatchEvent(
      new CustomEvent('searchrequest-filter-applied', {
        detail: {
          filters_object: pendingFilters,
          type: isSearchSidebar ? 'search' : 'plp'
        }
      })
    );
  };

  // Sync pendingFilters to activeFilters when sidebar opens or filters are applied
  useEffect(() => {
    if (isVisible) {
      setPendingFilters(activeFilters);
    }
  }, [isVisible, activeFilters]);

  // Collapsible Filter Component
  const CollapsibleFilter = ({ filter, metaobjectFilter }) => {
    const [isOpen, setIsOpen] = useState(true);
    const displayName = metaobjectFilter?.display_name || filter.label;

    return (
      <div className='transition-all duration-200 -mx-2 px-2 py-1 rounded-lg filter-group'>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className='w-full flex items-center justify-between py-2 text-left focus:outline-none'
        >
          <span className='b7--medium'>{displayName}</span>
          <i
            className={`icon ${
              isOpen ? 'icon-chevron-up' : 'icon-chevron-down'
            } text-2xl transition-transform duration-200`}
          ></i>
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className='-mt-4'>{renderFilterValues(filter, metaobject_filters)}</div>
        </div>
      </div>
    );
  };

  // Render filter groups with configurable dividers
  const renderFilterGroups = () => {
    if (!filters || filters.length === 0) return null;
    return filters.map((filter, idx) => {
      const metaobjectFilter = metaobject_filters?.find(
        (value) => value.filter_name?.toLowerCase() === filter.key?.toLowerCase()
      );

      return (
        <React.Fragment key={filter.key}>
          <CollapsibleFilter
            filter={filter}
            metaobjectFilter={metaobjectFilter}
          />
          {show_filter_dividers && filter_divider_style && (
            <div
              style={{ width: '100%', borderBottom: `${filter_divider_style}` }}
              className='filter-divider'
            />
          )}
        </React.Fragment>
      );
    });
  };

  return shouldRender ? (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out z-40 ${
          isVisible && !isClosing ? 'opacity-30' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleInternalClose}
      ></div>
      {/* Sidebar */}
      <div
        className={`collection-sidebar fixed top-0 flex flex-col ${
          sidebarPosition === 'left' ? 'left-0' : 'right-0'
        } overflow-y-auto w-full md:w-[480px] h-full bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 transform ${animationClass}`}
      >
        <div className='grow'>
          {/* Header */}
          <div className='sticky top-0 bg-white border-b border-gray-100 z-10 shadow-sm'>
            <div className='px-[24px] py-[24px] flex justify-between items-center'>
              <div className='flex items-center space-x-1'>
                <h3 className='b6--medium'>Filter</h3>
                {activeFilterCount > 0 && <span className='b6--medium'>({activeFilterCount})</span>}
              </div>
              <button
                onClick={handleInternalClose}
                className='rounded-full transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
                aria-label='Close filters'
              >
                <span className='icon icon-close text-[30px] text-black transition-colors duration-200'></span>
              </button>
            </div>
          </div>
          {/* Loading overlay */}
          {loading && (
            <div className='absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-20'>
              <div className='w-8 h-8 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-3'></div>
              <span className='b7--medium'>Updating filters...</span>
            </div>
          )}
          {/* Filter content */}
          <div className='facets flex flex-col px-4 lg:px-6 pt-2 lg:pt-3 space-y-2'>
            {/* Active filters summary */}
            {(applyInstantly ? activeFilterCount > 0 : pendingFilters && Object?.keys(pendingFilters).length > 0) && (
              <div className='active-filters'>
                <div className='b7--medium flex items-center justify-between mb-3 hidden'>
                  <span className='b7--medium'>Active Filters</span>
                </div>
                <div className='flex flex-wrap gap-2 md:gap-3'>
                  {(applyInstantly ? Object.entries(activeFilters) : Object.entries(pendingFilters)).map(
                    ([filterKey, values]) =>
                      (Array.isArray(values) ? values : []).map((value, index) => {
                        const readableValue = getDisplayValueForActiveFilterLocal(filterKey, value);
                        return (
                          <span
                            key={`${filterKey}-${value}-${index}`}
                            className={`active-filter-item ${isMobile ? 'b8--medium' : 'b7--medium'} group`}
                          >
                            {readableValue}
                            <button
                              className='ml-2'
                              onClick={(e) => {
                                e.stopPropagation();
                                if (applyInstantly) {
                                  handleFilterChangeWithFeedback(filterKey, value);
                                } else {
                                  setPendingFilters((prev) => {
                                    const updated = { ...prev };
                                    updated[filterKey] = (updated[filterKey] || []).filter((v) => v !== value);
                                    if (updated[filterKey].length === 0) delete updated[filterKey];
                                    return updated;
                                  });
                                }
                              }}
                            >
                              <i className='icon icon-close text-xs'></i>
                            </button>
                          </span>
                        );
                      })
                  )}
                </div>
                {show_filter_dividers && filter_divider_style && (
                  <div
                    style={{ width: '100%', borderBottom: `${filter_divider_style}` }}
                    className='filter-divider mt-2 lg:mt-3'
                  />
                )}
              </div>
            )}
            {/* Filter groups with dividers */}
            {loading && (!filters || filters.length === 0) ? (
              // Show skeleton loading when filters are loading
              <div className='space-y-6'>
                <FilterSkeleton type='list' />
                <FilterSkeleton type='button' />
                <FilterSkeleton type='size' />
              </div>
            ) : filters && filters.length > 0 ? (
              renderFilterGroups()
            ) : (
              <div className='py-12 text-center'>
                <div className='b7--medium mb-2'>
                  <i className='icon icon-filter text-3xl'></i>
                </div>
                <div className='b7--medium'>No filters available</div>
                <div className='b7--medium'>Try adjusting your search criteria</div>
              </div>
            )}
          </div>
        </div>
        {/* Footer */}
        <div className='sticky z-10 bg-white w-full bottom-0 py-4 px-6 b6--bold shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]'>
          <div className='flex space-x-3 justify-between'>
            <div className='flex w-1/3'>
              {(applyInstantly ? activeFilterCount > 0 : pendingFilters && Object.keys(pendingFilters).length > 0) && (
                <button
                  className='underline text-[12px] md:no-underline md:text-[16px]'
                  onClick={clearAllFiltersWithFeedback}
                  disabled={loading}
                >
                  Remove All
                </button>
              )}
            </div>
            <button
              className='button button-outline text-[12px] lg:text-[16px]'
              onClick={
                applyInstantly
                  ? handleInternalClose
                  : () => {
                      handleApplyPendingFilters();
                      handleInternalClose();
                    }
              }
            >
              <span>
                {applyInstantly ? 'Close Filters' : 'Apply'}{' '}
                {/* {totalResults > 0 && <span className='product-count-filters'>{totalResults}</span>} Products */}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  ) : null;
};
